import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission, ASSESSMENT_PERMISSIONS } from '../../lib/permissions';
import {
  PlusIcon,
  PencilIcon,
  EyeIcon,
  TrashIcon,
  ClockIcon,
  UserIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { useJobs } from '../../hooks/useJobs';

const Assessments = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [openDropdowns, setOpenDropdowns] = useState(new Set());

  const canView = hasPermission(user, ASSESSMENT_PERMISSIONS.VIEW);
  const canCreate = hasPermission(user, ASSESSMENT_PERMISSIONS.CREATE);
  const canEdit = hasPermission(user, ASSESSMENT_PERMISSIONS.EDIT);
  const canDelete = hasPermission(user, ASSESSMENT_PERMISSIONS.DELETE);
  const canInvite = hasPermission(user, ASSESSMENT_PERMISSIONS.INVITE);
  const canPublish = hasPermission(user, ASSESSMENT_PERMISSIONS.PUBLISH);
  const canResults = hasPermission(user, ASSESSMENT_PERMISSIONS.RESULTS);

  useEffect(() => {
    if (user && !canView) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, canView, navigate]);

  // Fetch jobs (only if user has job.view) and assessments – shared hook so jobs list is reused
  const {
    data: jobsData,
    isLoading: jobsLoading,
    error: jobsError,
  } = useJobs(canView && !!user && hasPermission(user, 'job.view'));

  const {
    data: assessmentsData,
    isLoading: assessmentsLoading,
    error: assessmentsError,
  } = useQuery({
    queryKey: ['assessments-batch'],
    queryFn: async () => {
      const response = await axios.get('/api/assessments/batch/by-jobs');
      return response.data?.assessmentsByJob || {};
    },
    staleTime: 2 * 60 * 1000,
    enabled: canView && !!user,
  });

  const jobs = jobsData || [];
  const jobAssessments = assessmentsData || {};
  const loading = jobsLoading || assessmentsLoading;

  useEffect(() => {
    const handleClickOutside = () => {
      closeAllDropdowns();
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Mutation for deleting assessment
  const deleteAssessmentMutation = useMutation({
    mutationFn: async (assessmentId) => {
      await axios.delete(`/api/assessments/${assessmentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments-batch'] });
      toast.success('Assessment deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting assessment:', error);
      toast.error('Failed to delete assessment');
    },
  });

  // Mutation for ending assessment
  const endAssessmentMutation = useMutation({
    mutationFn: async (assessmentId) => {
      const response = await axios.put(`/api/assessments/${assessmentId}/end`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments-batch'] });
      toast.success('Assessment ended successfully');
    },
    onError: (error) => {
      console.error('❌ Error ending assessment:', error);
      toast.error('Failed to end assessment');
    },
  });

  const handleDeleteAssessment = async (assessmentId) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this assessment? This action cannot be undone.'
      )
    ) {
      return;
    }
    deleteAssessmentMutation.mutate(assessmentId);
  };

  const handleEndAssessment = async (assessmentId) => {
    if (
      !window.confirm(
        'Are you sure you want to end this assessment? This will prevent new candidates from taking it, but existing candidates can still complete it.'
      )
    ) {
      return;
    }
    endAssessmentMutation.mutate(assessmentId);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'published':
        return 'bg-green-100 text-green-800';
      case 'ended':
        return 'bg-red-100 text-red-800';
      case 'archived':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const toggleDropdown = (assessmentId) => {
    setOpenDropdowns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(assessmentId)) {
        newSet.delete(assessmentId);
      } else {
        newSet.add(assessmentId);
      }
      return newSet;
    });
  };

  const closeAllDropdowns = () => {
    setOpenDropdowns(new Set());
  };

  // Filter jobs by search (when jobs are loaded)
  const filteredJobs = jobs.filter(
    (job) =>
      job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (jobAssessments[job._id] || []).some((a) =>
        a.title?.toLowerCase().includes(searchTerm.toLowerCase())
      )
  );

  const placeholderJob = { _id: '', title: '—', location: '', locationType: '' };

  // Flatten assessments for table view (and keep link to job)
  const filteredAssessments =
    jobs.length > 0
      ? [
          ...filteredJobs.reduce((rows, job) => {
            const list = jobAssessments[job._id] || [];
            list.forEach((assessment) => {
              rows.push({ assessment, job });
            });
            return rows;
          }, []),
          ...(jobAssessments['no-job'] || [])
            .filter((a) =>
              searchTerm ? (a.title || '').toLowerCase().includes(searchTerm.toLowerCase()) : true
            )
            .map((assessment) => ({ assessment, job: placeholderJob })),
        ]
      : Object.entries(jobAssessments).reduce((rows, [jobId, list]) => {
          list
            .filter((a) =>
              searchTerm ? (a.title || '').toLowerCase().includes(searchTerm.toLowerCase()) : true
            )
            .forEach((assessment) => {
              rows.push({ assessment, job: placeholderJob });
            });
          return rows;
        }, []);

  if (user && !canView) {
    return null;
  }

  // Show error state if both queries failed
  if ((jobsError || assessmentsError) && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="w-full px-2 sm:px-3 lg:px-4">
          <div className="bg-white rounded-xl shadow-sm border border-red-200 text-center py-20">
            <p className="text-red-600 font-medium">
              Failed to load assessments. Please try refreshing the page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="w-full px-2 sm:px-3 lg:px-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-indigo-600"></div>
            <p className="mt-4 text-gray-600 font-medium">Loading assessments...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full px-2 sm:px-3 lg:px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
                <DocumentTextIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Assessments</h1>
                <p className="text-sm text-gray-600 mt-1">
                  View and manage assessments linked to your job posts.
                </p>
              </div>
            </div>
            {canCreate && (
              <button
                type="button"
                disabled
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-gray-300 text-sm font-medium text-gray-500 bg-gray-100 cursor-not-allowed"
              >
                <PlusIcon className="h-4 w-4" />
                <span>Create assessment (coming soon)</span>
              </button>
            )}
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-sm mb-6 p-4 border border-gray-200">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by assessment name or job title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>
        </div>

        {/* Assessments Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">All assessments</h2>
          </div>

          {filteredAssessments.length === 0 ? (
            <div className="text-center py-12 px-4">
              <div className="flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mx-auto mb-4">
                <DocumentTextIcon className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">No assessments found</h3>
              <p className="text-sm text-gray-600 max-w-md mx-auto">
                Once you start creating assessments, they will appear here along with the job post
                they are linked to.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-auto divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Assessment
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Job Post
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Questions
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAssessments.map(({ assessment, job }) => (
                    <tr key={assessment._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 align-top">
                        <div className="text-sm font-medium text-gray-900 break-words whitespace-normal">
                          {assessment.title || 'Untitled assessment'}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="text-sm text-gray-900 break-words whitespace-normal">
                          {job.title}
                        </div>
                        <div className="mt-0.5 text-xs text-gray-500">
                          {job.location || 'Remote'} •{' '}
                          <span className="capitalize">{job.locationType}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            assessment.status
                          )}`}
                        >
                          {assessment.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top text-center">
                        <span className="text-sm text-gray-900">
                          {assessment.totalQuestions || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top text-center">
                        <span className="text-sm text-gray-900">
                          {assessment.duration ? `${assessment.duration} min` : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            {assessment.status === 'draft' && canEdit && (
                              <button
                                onClick={() =>
                                  navigate(`/dashboard/assessments/${assessment._id}/build`)
                                }
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-indigo-200 text-indigo-600 bg-white hover:bg-indigo-50 hover:border-indigo-300"
                              >
                                <PencilSquareIcon className="h-3.5 w-3.5" />
                                <span>Build</span>
                              </button>
                            )}
                            {assessment.status === 'published' && (
                              <>
                                {canResults && (
                                  <button
                                    onClick={() =>
                                      navigate(`/dashboard/assessments/${assessment._id}/view`)
                                    }
                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-gray-200 text-gray-700 bg-white hover:bg-gray-50"
                                  >
                                    <EyeIcon className="h-3.5 w-3.5" />
                                    <span>View</span>
                                  </button>
                                )}
                                {canInvite && (
                                  <button
                                    onClick={() =>
                                      navigate(`/dashboard/assessments/${assessment._id}/invite`)
                                    }
                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-indigo-200 text-indigo-600 bg-white hover:bg-indigo-50 hover:border-indigo-300"
                                  >
                                    <UserIcon className="h-3.5 w-3.5" />
                                    <span>Invite</span>
                                  </button>
                                )}
                              </>
                            )}
                            {assessment.status === 'ended' && canResults && (
                              <button
                                onClick={() =>
                                  navigate(`/dashboard/assessments/${assessment._id}/view`)
                                }
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-gray-200 text-gray-700 bg-white hover:bg-gray-50"
                              >
                                <EyeIcon className="h-3.5 w-3.5" />
                                <span>View</span>
                              </button>
                            )}
                          </div>
                          <div className="relative">
                            {(canEdit || canDelete) && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleDropdown(assessment._id);
                                  }}
                                  className="p-1.5 border border-gray-300 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
                                >
                                  <EllipsisVerticalIcon className="h-4 w-4" />
                                </button>
                                {openDropdowns.has(assessment._id) && (
                                  <div className="absolute right-0 mt-2 w-44 bg-white rounded-lg shadow-xl border border-gray-200 z-10">
                                    <div className="py-1">
                                      {canEdit && (
                                        <button
                                          onClick={() => {
                                            navigate(
                                              `/dashboard/assessments/${assessment._id}/edit`
                                            );
                                            closeAllDropdowns();
                                          }}
                                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        >
                                          <PencilIcon className="h-4 w-4 mr-3" /> Edit
                                        </button>
                                      )}
                                      {assessment.status === 'published' && canEdit && (
                                        <button
                                          onClick={() => {
                                            handleEndAssessment(assessment._id);
                                            closeAllDropdowns();
                                          }}
                                          className="flex items-center w-full px-4 py-2 text-sm text-orange-600 hover:bg-orange-50"
                                        >
                                          <ClockIcon className="h-4 w-4 mr-3" /> End Assessment
                                        </button>
                                      )}
                                      {canDelete && (
                                        <>
                                          <div className="border-t border-gray-100 my-1" />
                                          <button
                                            onClick={() => {
                                              handleDeleteAssessment(assessment._id);
                                              closeAllDropdowns();
                                            }}
                                            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                          >
                                            <TrashIcon className="h-4 w-4 mr-3" /> Delete
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Assessments;
